import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const InterestCalculatorPage = () => {
  const [initialInvestment, setInitialInvestment] = useState('20000');
  const [annualContribution, setAnnualContribution] = useState('5000');
  const [monthlyContribution, setMonthlyContribution] = useState('');
  const [contributeAt, setContributeAt] = useState('end');
  const [interestRate, setInterestRate] = useState('5');
  const [compound, setCompound] = useState('monthly');
  const [investmentLength, setInvestmentLength] = useState('5');
  const [lengthType, setLengthType] = useState('years');
  const [taxRate, setTaxRate] = useState('');
  const [inflationRate, setInflationRate] = useState('');
  const [result, setResult] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleView, setScheduleView] = useState('yearly');

  const compoundOptions = [
    { value: 'annually', label: 'Annually (APY)', n: 1 },
    { value: 'semiannually', label: 'Semi-annually', n: 2 },
    { value: 'quarterly', label: 'Quarterly', n: 4 },
    { value: 'monthly', label: 'Monthly (APR)', n: 12 },
    { value: 'semimonthly', label: 'Semi-monthly', n: 24 },
    { value: 'biweekly', label: 'Biweekly', n: 26 },
    { value: 'weekly', label: 'Weekly', n: 52 },
    { value: 'daily', label: 'Daily', n: 365 },
    { value: 'continuously', label: 'Continuously', n: Infinity }
  ];

  const calculate = () => {
    const P = parseFloat(initialInvestment) || 0;
    const annualContrib = parseFloat(annualContribution) || 0;
    const monthlyContrib = parseFloat(monthlyContribution) || 0;
    const r = (parseFloat(interestRate) || 0) / 100;
    const months = lengthType === 'years' ? parseFloat(investmentLength) * 12 : parseFloat(investmentLength);
    const years = months / 12;
    const tax = (parseFloat(taxRate) || 0) / 100;
    const inflation = (parseFloat(inflationRate) || 0) / 100;
    const n = compoundOptions.find(c => c.value === compound)?.n || 12;

    const schedule = [];
    let balance = P;
    let totalContributions = P;
    let totalInterest = 0;
    let interestOnInitial = 0;
    let interestOnContributions = 0;
    
    const periodsPerYear = compound === 'continuously' ? 12 : n;
    const ratePerPeriod = compound === 'continuously' ? r / 12 : r / n;
    const periodsPerMonth = periodsPerYear / 12;
    
    for (let month = 1; month <= months; month++) {
      const isYearStart = month % 12 === 1;
      const isYearEnd = month % 12 === 0;
      const currentYear = Math.ceil(month / 12);
      
      let monthlyDeposit = 0;
      if (month === 1 || isYearStart) {
        monthlyDeposit += annualContrib;
      }
      monthlyDeposit += monthlyContrib;
      
      if (contributeAt === 'beginning' && monthlyDeposit > 0) {
        balance += monthlyDeposit;
        totalContributions += monthlyDeposit;
      }
      
      let interest;
      if (compound === 'continuously') {
        interest = balance * (Math.exp(r / 12) - 1);
      } else {
        interest = balance * ratePerPeriod * periodsPerMonth;
      }
      
      if (tax > 0) {
        interest = interest * (1 - tax);
      }
      
      totalInterest += interest;
      balance += interest;
      
      if (contributeAt === 'end' && monthlyDeposit > 0) {
        balance += monthlyDeposit;
        totalContributions += monthlyDeposit;
      }
      
      schedule.push({
        month,
        year: currentYear,
        deposit: monthlyDeposit,
        interest,
        balance,
        isYearEnd
      });
    }
    
    const buyingPower = inflation > 0 ? balance / Math.pow(1 + inflation, years) : balance;
    
    const initialPercent = ((P / balance) * 100).toFixed(0);
    const contribPercent = (((totalContributions - P) / balance) * 100).toFixed(0);
    const interestPercent = ((totalInterest / balance) * 100).toFixed(0);
    
    const yearlySchedule = [];
    for (let year = 1; year <= years; year++) {
      const yearData = schedule.filter(s => s.year === year);
      if (yearData.length > 0) {
        yearlySchedule.push({
          year,
          deposit: yearData.reduce((sum, m) => sum + m.deposit, 0),
          interest: yearData.reduce((sum, m) => sum + m.interest, 0),
          balance: yearData[yearData.length - 1].balance
        });
      }
    }

    setResult({
      endingBalance: balance,
      totalPrincipal: P,
      totalContributions: totalContributions - P,
      totalInterest,
      buyingPower,
      initialPercent,
      contribPercent,
      interestPercent,
      schedule,
      yearlySchedule
    });
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(num);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/financial-calculators" className="text-cyan-400 hover:text-cyan-300">Financial</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Interest Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Interest Calculator</h1>
          <p className="text-gray-400 mb-6">
            This Compound Interest Calculator can help determine the compound interest accumulation and final balances on both fixed principal amounts and additional periodic contributions.
          </p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-36">Initial Investment</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input type="number" value={initialInvestment} onChange={(e) => setInitialInvestment(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-36">Annual Contribution</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input type="number" value={annualContribution} onChange={(e) => setAnnualContribution(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="0" />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-36">Monthly Contribution</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">$</span>
                    <Input type="number" value={monthlyContribution} onChange={(e) => setMonthlyContribution(e.target.value)}
                      className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="0" />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-36">Contribute at the</label>
                  <select value={contributeAt} onChange={(e) => setContributeAt(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                    <option value="beginning">Beginning</option>
                    <option value="end">End</option>
                  </select>
                  <span className="text-gray-400 text-sm">of each period</span>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-36">Interest Rate</label>
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.01" value={interestRate} onChange={(e) => setInterestRate(e.target.value)}
                      className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <span className="text-gray-400">%</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-36">Compound</label>
                  <select value={compound} onChange={(e) => setCompound(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                    {compoundOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-36">Investment Length</label>
                  <div className="flex items-center gap-2">
                    <Input type="number" value={investmentLength} onChange={(e) => setInvestmentLength(e.target.value)}
                      className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                    <select value={lengthType} onChange={(e) => setLengthType(e.target.value)}
                      className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                      <option value="years">years</option>
                      <option value="months">months</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-36">Tax Rate</label>
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)}
                      className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="0" />
                    <span className="text-gray-400">%</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-gray-300 font-medium w-36">Inflation Rate</label>
                  <div className="flex items-center gap-1">
                    <Input type="number" step="0.01" value={inflationRate} onChange={(e) => setInflationRate(e.target.value)}
                      className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="0" />
                    <span className="text-gray-400">%</span>
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">
              Calculate
            </Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <h3 className="text-lg font-semibold text-white">Results</h3>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Ending Balance</span>
                      <span className="font-bold text-green-400 text-lg">{formatCurrency(result.endingBalance)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total Principal</span>
                      <span className="font-semibold text-white">{formatCurrency(result.totalPrincipal)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total Contributions</span>
                      <span className="font-semibold text-white">{formatCurrency(result.totalContributions)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total Interest</span>
                      <span className="font-bold text-cyan-400">{formatCurrency(result.totalInterest)}</span>
                    </div>
                    {inflationRate && (
                      <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                        <span className="text-gray-400">Buying Power (inflation adjusted)</span>
                        <span className="font-semibold text-white">{formatCurrency(result.buyingPower)}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-center justify-center gap-4">
                    <div className="relative w-32 h-32">
                      <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#06b6d4" strokeWidth="20"
                          strokeDasharray={`${result.initialPercent * 2.51} 251`} />
                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#8b5cf6" strokeWidth="20"
                          strokeDasharray={`${result.contribPercent * 2.51} 251`}
                          strokeDashoffset={`-${result.initialPercent * 2.51}`} />
                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#f59e0b" strokeWidth="20"
                          strokeDasharray={`${result.interestPercent * 2.51} 251`}
                          strokeDashoffset={`-${(parseFloat(result.initialPercent) + parseFloat(result.contribPercent)) * 2.51}`} />
                      </svg>
                    </div>
                    <div className="text-xs space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-cyan-500 rounded"></div>
                        <span className="text-gray-400">{result.initialPercent}% Initial Investment</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-purple-500 rounded"></div>
                        <span className="text-gray-400">{result.contribPercent}% Contributions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-amber-500 rounded"></div>
                        <span className="text-gray-400">{result.interestPercent}% Interest</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <AdInContent />

          {result && (
            <div className="mt-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Accumulation Schedule</h2>
                <button onClick={() => setShowSchedule(!showSchedule)} className="text-cyan-400 hover:text-cyan-300 text-sm">
                  {showSchedule ? 'Hide' : 'Show'} Schedule
                </button>
              </div>

              {showSchedule && (
                <>
                  <div className="flex gap-4 mb-4">
                    <button onClick={() => setScheduleView('yearly')}
                      className={`px-4 py-2 rounded text-sm ${scheduleView === 'yearly' ? 'bg-cyan-600 text-white' : 'bg-[#2a2a2a] text-gray-400'}`}>
                      Annual Schedule
                    </button>
                    <button onClick={() => setScheduleView('monthly')}
                      className={`px-4 py-2 rounded text-sm ${scheduleView === 'monthly' ? 'bg-cyan-600 text-white' : 'bg-[#2a2a2a] text-gray-400'}`}>
                      Monthly Schedule
                    </button>
                  </div>

                  <div className="overflow-x-auto max-h-80 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-[#1a1a1a]">
                        <tr className="border-b border-[#2a2a2a]">
                          <th className="text-left py-2 px-2 text-gray-400">{scheduleView === 'yearly' ? 'Year' : 'Month'}</th>
                          <th className="text-right py-2 px-2 text-gray-400">Deposit</th>
                          <th className="text-right py-2 px-2 text-gray-400">Interest</th>
                          <th className="text-right py-2 px-2 text-gray-400">Ending Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scheduleView === 'yearly' ? (
                          result.yearlySchedule.map((row) => (
                            <tr key={row.year} className="border-b border-[#2a2a2a]/50 hover:bg-[#2a2a2a]/30">
                              <td className="py-2 px-2 text-white">{row.year}</td>
                              <td className="py-2 px-2 text-right text-gray-300">{formatCurrency(row.deposit)}</td>
                              <td className="py-2 px-2 text-right text-gray-300">{formatCurrency(row.interest)}</td>
                              <td className="py-2 px-2 text-right text-white font-medium">{formatCurrency(row.balance)}</td>
                            </tr>
                          ))
                        ) : (
                          result.schedule.slice(0, 60).map((row) => (
                            <React.Fragment key={row.month}>
                              <tr className="border-b border-[#2a2a2a]/50 hover:bg-[#2a2a2a]/30">
                                <td className="py-1.5 px-2 text-white">{row.month}</td>
                                <td className="py-1.5 px-2 text-right text-gray-300">{formatCurrency(row.deposit)}</td>
                                <td className="py-1.5 px-2 text-right text-gray-300">{formatCurrency(row.interest)}</td>
                                <td className="py-1.5 px-2 text-right text-white font-medium">{formatCurrency(row.balance)}</td>
                              </tr>
                              {row.isYearEnd && (
                                <tr className="bg-[#0d0d0d]">
                                  <td colSpan="4" className="py-1 px-2 text-center text-gray-500 text-xs">End of year {row.year}</td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2 text-sm">
            <span className="text-gray-500">Related:</span>
            <Link to="/compound-interest-calculator" className="text-cyan-400 hover:text-cyan-300">Compound Interest</Link>
            <span className="text-gray-600">|</span>
            <Link to="/investment-calculator" className="text-cyan-400 hover:text-cyan-300">Investment</Link>
            <span className="text-gray-600">|</span>
            <Link to="/interest-rate-calculator" className="text-cyan-400 hover:text-cyan-300">Interest Rate</Link>
          </div>

          <div className="mt-10 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">Compound Interest</h2>
              <p className="text-gray-400 leading-relaxed mb-4">
                Interest is the compensation paid by the borrower to the lender for the use of money as a percent or an amount. 
                Compound interest is interest that is earned not only on the initial principal but also on accumulated interest 
                from previous periods.
              </p>
              <p className="text-gray-400 leading-relaxed">
                The more frequently interest is compounded within a time period, the higher the interest will be earned on an 
                original principal. This is the power of compound interest.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">The Rule of 72</h2>
              <p className="text-gray-400 leading-relaxed">
                To estimate how long it takes to double your money, divide 72 by the interest rate. For example, at 8% interest, 
                72 ÷ 8 = 9 years to double your investment.
              </p>
            </section>
          </div>

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'Mortgage', path: '/mortgage-calculator' },
                { name: 'Loan', path: '/loan-calculator' },
                { name: 'Interest', path: '/interest-calculator' },
                { name: 'Investment', path: '/investment-calculator' },
                { name: 'Compound Interest', path: '/compound-interest-calculator' },
                { name: 'Retirement', path: '/retirement-calculator' },
              ].map((calc) => (
                <Link key={calc.path} to={calc.path}
                  className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 hover:border-cyan-500/50 text-sm transition-colors">
                  {calc.name}
                </Link>
              ))}
            </div>
          </div>

          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default InterestCalculatorPage;
